import { FormEvent, useContext, useState } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Field, Modal, PasswordInput } from "../ui";
import { useNextT } from "../i18n";
import { authPasswordActions } from "../bridges/authPassword";
import { useSettingsPage } from "../settings/pageApi";
import { DEFAULT_PASSWORD_MIN } from "../settings/components/PasswordDialog";
import s from "./public.module.scss";

/** True when this tab's sign-in came from a one-time link (see the token sign-in in authSaga). */
const arrivedByLink = (): boolean => {
  try { return sessionStorage.getItem("cy_signed_in_by_link") === "1"; } catch { return false; }
};

/**
 * P4.4 (owner): the "Set a password" prompt, once, in the new look. Words fit how the person arrived (a link, or added by a
 * manager), "Not now" closes it, and it says where to do it later ("My settings"). Opened by the same store flag as today's.
 */
export const SetPasswordDialog = () => (useContext(QueryClientContext) ? <WithServerRule /> : <Prompt min={DEFAULT_PASSWORD_MIN} />);

/** The rule's number is the server's (M3.1: 10 at release), asked only while the prompt is open. */
const WithServerRule = () => {
  const open = useSelector((st: any) => !!st.authStore.showSetPasswordModal);
  return <Prompt min={useSettingsPage(open).data?.password_min_length ?? DEFAULT_PASSWORD_MIN} />;
};

const Prompt = ({ min: MIN }: { min: number }) => {
  const { t } = useNextT();
  const { t: tRoot } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector((st: any) => !!st.authStore.showSetPasswordModal);
  const saving = useSelector((st: any) => !!st.authStore.passwordSaving);
  const serverError = useSelector((st: any) => st.authStore.passwordError as string | null);
  const [password, setPassword] = useState("");
  const [tried, setTried] = useState(false);

  const close = () => dispatch(authPasswordActions.closeSetPasswordModal());
  const short = tried && password.length < MIN;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (password.length < MIN) return;
    dispatch(authPasswordActions.updatePassword({ password, password_confirmation: password }));
  };

  return (
    <Modal open={open} title={tRoot("workspaceSettings.set_password_modal_title")} onClose={close} size="sm"
      actions={(
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>{t("public.setpw.not_now")}</Button>
          <Button variant="primary" type="submit" form="set-password-once" loading={saving}>{t("settingsPage.password.save")}</Button>
        </>
      )}>
      <form id="set-password-once" className={s.pwForm} onSubmit={submit} noValidate>
        <p className={s.linkNote}>{arrivedByLink() ? t("public.setpw.by_link") : t("public.setpw.by_manager")}</p>
        {/* M3.1–M3.3: one field with an eye button; the rule once, as the hint or (red) as the error. */}
        <Field label={t("settingsPage.password.new")} hint={short ? undefined : t("settingsPage.password.hint", { min: MIN })} error={short ? t("settingsPage.password.hint", { min: MIN }) : undefined}>
          {(f) => <PasswordInput id={f.id} showLabel={t("settingsPage.password.show")} hideLabel={t("settingsPage.password.hide")} aria-describedby={f.describedBy} invalid={f.invalid} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        {serverError && <p role="alert" className={s.linkNote}>{serverError}</p>}
        <p className={s.linkNote}>{t("public.setpw.later")}</p>
      </form>
    </Modal>
  );
};
